/**
 * Interviews across submissions.
 *
 * This is the recruiter's own pipeline, not an admin screen, so it sits in the
 * main nav rather than under /admin and is scoped server-side to whatever the
 * viewer owns — their own for a recruiter, their reporting subtree for a
 * manager, the whole company for an admin.
 *
 * The default view is what is still outstanding: an interview with no result
 * recorded, however long ago it was scheduled. That is deliberately not a date
 * filter — an interview last week that nobody wrote up is exactly the one
 * still needing a chase, and hiding it because its date has passed is how it
 * gets forgotten.
 *
 * Scheduling one also puts a reminder on the worklist. That happens server
 * side; nothing here creates followups.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Loader2, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { titleCaseName } from '../lib/titleCase';
import type {
  Interview,
  InterviewClashes,
  InterviewMode,
  Page,
  Submission,
  SubmissionStatus,
} from '../types';

/**
 * Three ways an interview happens, and the order the dropdown offers them.
 *
 * "Walk-in" was a fourth until the business asked for three. It described how
 * somebody arrived rather than how they were interviewed, so the same
 * conversation was filed two ways depending on who booked it; the rows that
 * held it were migrated to f2f. The importer still understands the word,
 * because our sheets still say it.
 */
const MODE_LABELS: Record<InterviewMode, string> = {
  telephonic: 'Telephonic',
  virtual: 'Virtual',
  f2f: 'Face to face',
};

/** Submissions worth scheduling against — the ones still in play. */
const OPEN_SUBMISSION_STATUSES = new Set([
  'pending',
  'shortlisted',
  'selected',
  'hold',
  'followup',
  'yet_to_join',
]);

/**
 * Where a submission can be moved to from here.
 *
 * Five of the eight, and the three omissions are the point.
 *
 * `yet_to_join` and `working` are excluded because they are owned elsewhere:
 * accepting an offer writes `yet_to_join`, and both sit in CHOSEN_STATUSES,
 * which drives the job-opening funnel counts. Setting either by hand would
 * claim somebody accepted an offer and started work with no Offer row and no
 * Placement behind it — a fabricated pipeline state that inflates the funnel
 * and puts a person on the Tracker with no placement to carry their DOJ or CTC.
 *
 * `followup` is excluded because nothing in the product ever writes it and it
 * is indistinguishable from `hold` in practice. Offering both invites two
 * people to mean different things by them. Easy to add later, hard to un-teach.
 *
 * Deliberately NOT OPEN_SUBMISSION_STATUSES above: that is a filter for
 * "worth scheduling an interview against", which correctly includes
 * `yet_to_join` and correctly excludes `rejected` — and a client saying no is
 * the single most common thing this control will record.
 */
const SETTABLE_STATUSES: SubmissionStatus[] = [
  'pending',
  'shortlisted',
  'selected',
  'hold',
  'rejected',
];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A status as words rather than an identifier. */
function label(value: string): string {
  return value.replace(/_/g, ' ');
}

/** The date an interview is on, whichever field carries it. */
function scheduleLabel(interview: Interview): string {
  if (interview.scheduledAt) {
    const at = new Date(interview.scheduledAt);
    return `${at.toLocaleDateString()} ${at.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }
  return interview.scheduledOn ?? 'not scheduled';
}

export default function InterviewsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [upcomingOnly, setUpcomingOnly] = useState(true);
  const [mineOnly, setMineOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const interviews = useQuery<Page<Interview>>({
    queryKey: ['interviews', upcomingOnly, mineOnly],
    queryFn: async () =>
      (await client.get('/api/v1/interviews', {
        params: {
          limit: 200,
          ...(upcomingOnly ? { upcomingOnly: true } : {}),
          ...(mineOnly ? { mineOnly: true } : {}),
        },
      })).data,
  });

  const schedule = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/interviews', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      // The server also created or moved a reminder on the worklist.
      queryClient.invalidateQueries({ queryKey: ['my-day'] });
      setShowForm(false);
      toast.success('Interview scheduled');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = interviews.data?.items ?? [];

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Interviews</h2>
            <p className="mt-1 max-w-2xl text-xs text-gray-500">
              Scheduling an interview puts a reminder on your day. Recording
              what came of it clears the reminder — until then it keeps
              surfacing, which is the point.
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'Schedule interview'}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={upcomingOnly}
              onChange={(e) => setUpcomingOnly(e.target.checked)}
            />
            Still outstanding
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
            />
            Only mine
          </label>
        </div>
      </section>

      {showForm && (
        <ScheduleForm submitting={schedule.isPending} onSubmit={schedule.mutate} />
      )}

      {interviews.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : interviews.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">Could not load interviews.</p>
          <button type="button" className="btn-ghost" onClick={() => void interviews.refetch()}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card text-center">
          <CalendarClock className="mx-auto h-6 w-6 text-gray-300" />
          <p className="mt-2 text-sm text-gray-600">
            {upcomingOnly
              ? 'Nothing outstanding. Every interview has an outcome recorded.'
              : 'No interviews yet.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th scope="col" className="px-4 py-2.5 font-medium">Candidate</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Client</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Round</th>
                <th scope="col" className="px-4 py-2.5 font-medium">When</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Mode</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Outcome</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((interview) => (
                <tr
                  key={interview.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setOpenId(interview.id)}
                >
                  <td className="px-4 py-2.5 text-gray-900">
                    {titleCaseName(interview.candidateName) ?? '—'}
                    {interview.appliedPosition && (
                      <span className="ml-1.5 text-xs text-gray-400">
                        {interview.appliedPosition}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">
                    {interview.clientName ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-gray-600">
                    {interview.roundNumber}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                    {scheduleLabel(interview)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">
                    {interview.mode ? MODE_LABELS[interview.mode] : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    {interview.result ? (
                      <span className="text-gray-900">{interview.result}</span>
                    ) : (
                      <span className="badge bg-amber-100 text-amber-700">awaiting</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && (
        <OutcomePanel
          interview={rows.find((i) => i.id === openId) as Interview}
          onClose={() => setOpenId(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['interviews'] });
            queryClient.invalidateQueries({ queryKey: ['my-day'] });
            setOpenId(null);
          }}
        />
      )}
    </div>
  );
}

function ScheduleForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    submissionId: '',
    roundNumber: 1,
    scheduledOn: today(),
    time: '',
    mode: '' as InterviewMode | '',
    panel: '',
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Interviews hang off a submission, and there is no submissions screen to
  // start from, so the picker lives here.
  const submissions = useQuery<Page<Submission>>({
    queryKey: ['open-submissions'],
    queryFn: async () =>
      (await client.get('/api/v1/submissions', { params: { limit: 200 } })).data,
  });

  const open = (submissions.data?.items ?? []).filter((s) =>
    OPEN_SUBMISSION_STATUSES.has(s.status),
  );

  /**
   * Is this candidate already booked near this slot?
   *
   * Scoped to the *candidate*, not to the recruiter, because the person who
   * cannot be in two places is the candidate — and two recruiters booking them
   * for different clients is exactly the collision neither of them can see on
   * any other screen.
   *
   * Asked from the form as it stands, so the warning arrives before the slot
   * is committed to somebody's diary. The candidate comes from the chosen
   * submission rather than being typed, which is also what stops this firing
   * before there is anything to ask about.
   */
  const chosen = open.find((s) => s.id === form.submissionId);
  const slotAt = form.time
    ? new Date(`${form.scheduledOn}T${form.time}`).toISOString()
    : null;
  const { data: clashes } = useQuery<InterviewClashes>({
    queryKey: [
      'interviews', 'clashes', chosen?.candidateId, slotAt ?? form.scheduledOn,
    ],
    enabled: Boolean(chosen?.candidateId) && Boolean(form.scheduledOn),
    staleTime: 30_000,
    queryFn: async () =>
      (
        await client.get('/api/v1/interviews/clashes', {
          params: slotAt
            ? { candidateId: chosen!.candidateId, scheduledAt: slotAt }
            : { candidateId: chosen!.candidateId, scheduledOn: form.scheduledOn },
        })
      ).data,
  });
  const collisions = clashes?.clashes ?? [];

  return (
    <form
      className="card space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        // A time is sent as scheduledAt, which the server prefers over the
        // date. Without one, the reminder lands at the start of the day.
        const scheduledAt = form.time
          ? new Date(`${form.scheduledOn}T${form.time}`).toISOString()
          : null;
        onSubmit({
          submissionId: form.submissionId,
          roundNumber: form.roundNumber,
          scheduledOn: form.scheduledOn || null,
          scheduledAt,
          mode: form.mode || null,
          panel: form.panel.trim() || null,
        });
      }}
    >
      <div>
        <label className="label" htmlFor="i-submission">Candidate and Client</label>
        <select
          id="i-submission"
          className="input"
          value={form.submissionId}
          onChange={(e) => set('submissionId', e.target.value)}
          required
        >
          <option value="">Select a submission…</option>
          {open.map((s) => (
            <option key={s.id} value={s.id}>
              {titleCaseName(s.candidateName) ?? s.candidateId} → {s.clientName ?? s.clientId}
              {s.appliedPosition ? ` · ${s.appliedPosition}` : ''}
            </option>
          ))}
        </select>
        {submissions.data && open.length === 0 && (
          <p className="mt-1 text-xs text-gray-500">
            No open submissions. A candidate has to be submitted to a client
            before there is anything to interview them for.
          </p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="label" htmlFor="i-round">Round</label>
            <input
              id="i-round"
              type="number"
              min={1}
              max={20}
              className="input"
              value={form.roundNumber}
              onChange={(e) => set('roundNumber', Number(e.target.value))}
            />
          </div>
          <div className="col-span-2">
            <label className="label" htmlFor="i-date">Date</label>
            <input
              id="i-date"
              type="date"
              className="input"
              value={form.scheduledOn}
              onChange={(e) => set('scheduledOn', e.target.value)}
              aria-describedby={collisions.length ? 'interview-clash' : undefined}
            />

            {/* This candidate is already booked near here.

                Amber and never blocking: two rounds back to back on one
                afternoon is real, and so is a morning with one client and an
                afternoon with another. What is not real is being in two
                places at once, and only whoever is booking can tell those
                apart.

                Says which precision it is comparing at, because "the same
                day" and "within ninety minutes" are different claims and the
                reader should not have to guess which one they are being
                given. */}
            {collisions.length > 0 && (
              <p
                id="interview-clash"
                role="status"
                className="mt-1 text-[11px] leading-relaxed text-amber-700"
              >
                Already booked{' '}
                {collisions[0].precision === 'time'
                  ? `within ${clashes?.windowMinutes ?? 90} minutes`
                  : 'on this day'}
                :{' '}
                {collisions.slice(0, 2).map((c, i) => (
                  <span key={c.interviewId}>
                    {i > 0 && '; '}
                    {c.clientName ?? 'another client'}
                    {` round ${c.roundNumber}`}
                    {c.scheduledAt
                      && ` at ${new Date(c.scheduledAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`}
                  </span>
                ))}
                {collisions.length > 2 && ` and ${collisions.length - 2} more`}.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label" htmlFor="i-time">Time (Optional)</label>
            <input
              id="i-time"
              type="time"
              className="input"
              value={form.time}
              onChange={(e) => set('time', e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Without one, the reminder lands at 9am.
            </p>
          </div>
          <div>
            <label className="label" htmlFor="i-mode">Mode</label>
            <select
              id="i-mode"
              className="input"
              value={form.mode}
              onChange={(e) => set('mode', e.target.value as InterviewMode | '')}
            >
              <option value="">Not decided</option>
              {(Object.keys(MODE_LABELS) as InterviewMode[]).map((m) => (
                <option key={m} value={m}>{MODE_LABELS[m]}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        <label className="label" htmlFor="i-panel">Panel</label>
        <input
          spellCheck={false}
          id="i-panel"
          className="input"
          placeholder="Who is interviewing"
          value={form.panel}
          onChange={(e) => set('panel', e.target.value)}
        />
      </div>

      <button
        type="submit"
        className="btn-primary"
        disabled={submitting || !form.submissionId}
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        Schedule
      </button>
    </form>
  );
}

/**
 * Record what came of an interview, or move it.
 *
 * Recording a result clears the reminder server-side, so the two are one
 * action rather than a save followed by a separate tidy-up.
 */
function OutcomePanel({
  interview,
  onClose,
  onSaved,
}: {
  interview: Interview;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [result, setResult] = useState(interview.result ?? '');
  const [feedback, setFeedback] = useState(interview.feedback ?? '');
  const [reschedule, setReschedule] = useState(interview.scheduledOn ?? '');
  const [status, setStatus] = useState<SubmissionStatus | ''>(
    interview.submissionStatus ?? '',
  );

  const statusChanged = Boolean(status) && status !== interview.submissionStatus;
  // Moving the status does not undo an offer or a placement — those rows
  // persist and would then disagree with the submission. Said before the save,
  // not after.
  const wouldContradict =
    statusChanged && (interview.hasOffer || interview.hasPlacement);

  /**
   * Two writes, deliberately two requests.
   *
   * The interview's free-text result and the submission's status are separate
   * records and separate decisions — the whole reason this control exists is
   * that the text has never driven the status and must not start now. Sending
   * them together would need an endpoint that owns both, which would be that
   * coupling under another name.
   */
  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const updated = (
        await client.patch(`/api/v1/interviews/${interview.id}`, body)
      ).data;
      if (statusChanged) {
        await client.patch(`/api/v1/submissions/${interview.submissionId}`, {
          status,
        });
      }
      return updated;
    },
    onSuccess: () => {
      toast.success(
        statusChanged ? `Saved — submission is now ${label(status)}` : 'Interview updated',
      );
      onSaved();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Users className="h-4 w-4 text-indigo-600" />
          {titleCaseName(interview.candidateName) ?? 'Interview'} · round {interview.roundNumber}
        </h3>
        <p className="mt-0.5 text-xs text-gray-500">
          {interview.clientName} · {scheduleLabel(interview)}
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="label" htmlFor="o-result">Outcome</label>
            <input
              spellCheck={true}
              id="o-result"
              className="input"
              placeholder="Selected, Rejected, Hold — whatever the client said"
              value={result}
              onChange={(e) => setResult(e.target.value)}
              maxLength={60}
            />
            <p className="mt-1 text-xs text-gray-500">
              Free text on purpose. Recording anything here clears the reminder.
            </p>
          </div>

          {/* Its own control, visually separated from the free text above.
              The outcome field is what the client said; this is what the
              pipeline does about it. Nothing infers one from the other. */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="label" htmlFor="o-status">Submission Status</label>
            <select
              id="o-status"
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value as SubmissionStatus | '')}
            >
              {SETTABLE_STATUSES.map((value) => (
                <option key={value} value={value}>{label(value)}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Set separately from the outcome above. Moving this to
              {' '}<strong>selected</strong> is what makes the candidate
              offerable.
            </p>
            {wouldContradict && (
              <p className="mt-2 rounded bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                This submission already has
                {interview.hasPlacement ? ' a placement' : ' an offer'} against
                it. Changing the status here does not undo it, so the two will
                disagree until somebody fixes the other one.
              </p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="o-feedback">Feedback</label>
            <textarea
              spellCheck={true}
              id="o-feedback"
              className="input"
              rows={3}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>

          {!interview.result && (
            <div>
              <label className="label" htmlFor="o-reschedule">Move To</label>
              <input
                id="o-reschedule"
                type="date"
                className="input"
                value={reschedule}
                onChange={(e) => setReschedule(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Rescheduling moves the existing reminder rather than adding one.
              </p>
            </div>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                ...(result.trim() ? { result: result.trim() } : {}),
                feedback: feedback.trim() || null,
                ...(!interview.result && reschedule && reschedule !== interview.scheduledOn
                  ? { scheduledOn: reschedule }
                  : {}),
              })
            }
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
